import { and, desc, eq, ne, or, sql } from "drizzle-orm";
import type React from "react";
import { createContext, useContext, useMemo } from "react";

import { db } from "@/db/db";
import { feed, feedGroupMembership, item, permission } from "@/db/schema";
import { useReactiveSelectQuery } from "@/models/hooks/useReactiveSelectQuery.ts";
import { myActiveFeedsQuery } from "@/models/repositories";
import { CurrentFeedContext } from "./StateProviders/currentFeedProvider";
import { WorkspaceContext } from "./StateProviders/workspaceProvider";

type LiveQueries = {
  fetchNextActiveChannel?: (groupId?: string) => Promise<string>;
  myActiveFeeds?: Awaited<ReturnType<typeof myActiveFeedsQuery>>;
  myActiveFeedsFiltered?: Awaited<ReturnType<typeof myActiveFeedsQuery>>;
  myActiveWriteFeeds?: Awaited<ReturnType<typeof myActiveFeedsQuery>>;
  myActiveWriteFeedsFiltered?: Awaited<ReturnType<typeof myActiveFeedsQuery>>;
  workspaceMembershipId?: string | null;
};

export const LiveQueryContext = createContext<LiveQueries>({});
type Props = {
  children: React.ReactNode | React.ReactNode[];
};
const LiveQueriesProvider = ({ children }: Props) => {
  const { currentWorkspaceId: workspaceId, myCurrentWorkspaceMembership } = useContext(WorkspaceContext);
  const { currentFeedId: feedId } = useContext(CurrentFeedContext);

  // List of active feeds that the current user has permissions to
  // Single query that includes alias counts for all feeds
  const allActiveFeeds =
    useReactiveSelectQuery(
      myActiveFeedsQuery,
      [{ workspaceId, myCurrentWorkspaceMembershipId: myCurrentWorkspaceMembership?.id, filterCommandAlias: false }],
      "LiveQueriesProvider:myActiveFeedsQuery",
    ) ?? [];

  const myActiveWriteFeeds =
    useReactiveSelectQuery(
      myActiveFeedsQuery,
      [
        {
          workspaceId,
          myCurrentWorkspaceMembershipId: myCurrentWorkspaceMembership?.id,
          filterCommandAlias: false,
          permissionName: "write",
        },
      ],
      "LiveQueriesProvider:myActiveFeedsQuery",
    ) ?? [];

  // Derive both datasets from the single query result
  const myActiveFeeds = allActiveFeeds;
  const myActiveFeedsFiltered = useMemo(
    () =>
      allActiveFeeds.filter((feed) => {
        const count = feed.aliasCount as number | null | undefined;
        return (count ?? 0) > 0;
      }),
    [allActiveFeeds],
  );

  const myActiveWriteFeedsFiltered = useMemo(
    () =>
      myActiveWriteFeeds.filter((feed) => {
        const count = feed.aliasCount as number | null | undefined;
        return (count ?? 0) > 0;
      }),
    [myActiveWriteFeeds],
  );

  // Fetch the next active channel available to the user
  const fetchNextActiveChannel = async (groupId?: string): Promise<string> => {
    const realOrFakeFeedId = !feedId ? "NULL" : feedId;
    const realOrFakeGroupId = !groupId ? "NULL" : groupId;

    const activeChannel = await db
      .select({
        id: feed.id,
        feedId: permission.feedId,
        workspaceMembershipId: permission.workspaceMembershipId,
        enabled: permission.enabled,
        title: feed.title,
        groupId: feedGroupMembership.groupId,
        latestActivity: sql`GREATEST(
        coalesce(${feed.updatedAt}, DATE '0001-01-01'),
        coalesce(${item.createdAt}, DATE '0001-01-01')
      ) as latestActivity`,
      })
      .from(permission)
      .innerJoin(feed, eq(feed.id, permission.feedId))
      .leftJoin(feedGroupMembership, eq(feed.id, feedGroupMembership.feedId))
      .leftJoin(item, eq(feed.id, item.feedId))
      .where(
        and(
          eq(permission.workspaceMembershipId, myCurrentWorkspaceMembership?.id),
          eq(permission.enabled, true),
          eq(feed.workspaceId, workspaceId),
          ne(feed.id, realOrFakeFeedId),
          or(ne(feedGroupMembership.groupId, realOrFakeGroupId), eq(feedGroupMembership.groupId, null)),
        ),
      )
      .groupBy(
        feed.id,
        permission.feedId,
        permission.workspaceMembershipId,
        permission.enabled,
        feedGroupMembership.groupId,
        item.createdAt,
      )
      .orderBy(desc(sql`latestActivity`))
      .limit(1)
      .execute();

    if (activeChannel?.length === 0) {
      return `/workspaces/${workspaceId}`;
    }
    return `/workspaces/${workspaceId}/feeds/${activeChannel[0]?.id}`;
  };

  const LiveQueryState: LiveQueries = {
    fetchNextActiveChannel,
    myActiveFeeds,
    myActiveFeedsFiltered,
    myActiveWriteFeedsFiltered,
    myActiveWriteFeeds,
    workspaceMembershipId: myCurrentWorkspaceMembership?.id,
  };

  return <LiveQueryContext.Provider value={LiveQueryState}>{children}</LiveQueryContext.Provider>;
};

export default LiveQueriesProvider;
